import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { TabThroughEventsProps } from './utils.types';
import { CalendarAppointmentEvent } from 'components/mrp/components/Calendar/Calendar.types';
import { CalendarDisplayData } from 'components/mrp/stores/calendarStore/calendarStore.types';
import { CalendarPosition } from '../CalendarNavigationHandler.types';
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';
import { useShallow } from 'zustand/shallow';

export const useTabThroughEvents = (
  calendars: CalendarDisplayData[],
  calendarEvents: Record<string, CalendarAppointmentEvent[]>
) => {
  const isAppointmentFormOpen = useAppointmentStore(
    useShallow((state) => !!state.appointment)
  );

  // Memoize sorted events - recompute only when calendars/events change
  const allEvents = useMemo(() => {
    return calendars.flatMap((calendar) => {
      const events = calendarEvents[calendar.calendarId] || [];
      return events.toSorted(sortEventsByStartThenEnd);
    });
  }, [calendars, calendarEvents]);

  // Memoize pure index finder
  const findCurrentEventIndex = useCallback(
    (
      backwards: boolean,
      event?: CalendarAppointmentEvent,
      position?: CalendarPosition
    ): number => {
      if (event) {
        return allEvents.findIndex(
          (e) => e.id === event.id && e.calendarId === event.calendarId
        );
      }

      if (position) {
        const startIndex = allEvents.findIndex(
          (e) =>
            e.calendarId === position.calendarId &&
            dayjs(e.start).isSameOrAfter(position.date)
        );

        if (startIndex !== -1) {
          return backwards ? startIndex : startIndex - 1;
        }
      }

      return -1;
    },
    [allEvents]
  );

  // Memoize pure next index calculator
  const getNextIndex = useCallback(
    (currentIndex: number, backwards: boolean): number => {
      const length = allEvents.length;
      if (!length) return -1;

      if (currentIndex === -1) {
        return backwards ? length - 1 : 0;
      }

      const direction = backwards ? -1 : 1;
      return (currentIndex + direction + length) % length;
    },
    [allEvents.length]
  );

  // Stable tab handler - only changes if deps change
  const tabThroughEvents = useCallback(
    ({
      event,
      backwards = false,
      position,
      setEvent,
      setPosition
    }: Omit<TabThroughEventsProps, 'calendarEvents' | 'calendars'>) => {
      if (!allEvents.length || isAppointmentFormOpen) return;

      const currentIndex = findCurrentEventIndex(backwards, event, position);
      const nextIndex = getNextIndex(currentIndex, backwards);
      const nextEvent = allEvents[nextIndex];

      setEvent(nextEvent);
      setPosition({
        calendarId: nextEvent.calendarId,
        date: dayjs(nextEvent.start)
      });
    },
    [allEvents, isAppointmentFormOpen, findCurrentEventIndex, getNextIndex]
  );

  return { tabThroughEvents, isTabThroughActive: !isAppointmentFormOpen };
};

// Helper function sorting comparator
const sortEventsByStartThenEnd = (
  a: CalendarAppointmentEvent,
  b: CalendarAppointmentEvent
) => {
  const startA = dayjs(a.start);
  const startB = dayjs(b.start);
  const startDiff = startA.diff(startB);

  if (startDiff !== 0) return startDiff;

  // Fallback to end time (longer duration first)
  return dayjs(b.end).diff(a.end);
};
